/**
 * Formatacao para exibicao (RNF15).
 *
 * Este modulo e a UNICA fronteira do frontend autorizada a transformar inteiro
 * em texto com virgula. A regra que vem do backend (src/domain/money.ts) vale
 * igual aqui: dinheiro transita como inteiro de centavos e so ganha separador
 * decimal na hora de virar pixel.
 *
 * Nenhuma funcao daqui participa de conta. Somar, calcular percentual ou
 * aplicar comissao sobre o resultado de um formatador e erro: o valor ja nao e
 * mais numero, e mesmo antes de virar texto o `cents / 100` introduziria o
 * problema classico do IEEE-754 (0.1 + 0.2 === 0.30000000000000004).
 *
 * Instancias de Intl sao criadas no modulo, e nao dentro da funcao: montar um
 * NumberFormat custa ordens de grandeza mais que usar um pronto, e a tabela de
 * leads chama isso uma vez por celula.
 */

import type {
  CampaignChannel,
  CampaignStatus,
  Provider,
  RecoveryJobStatus,
  RecoveryTier,
} from '../types';

const LOCALE = 'pt-BR';

const CURRENCY_FORMATTER = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const INTEGER_FORMATTER = new Intl.NumberFormat(LOCALE, {
  maximumFractionDigits: 0,
});

const COMPACT_FORMATTER = new Intl.NumberFormat(LOCALE, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const PERCENT_FORMATTER = new Intl.NumberFormat(LOCALE, {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  dateStyle: 'short',
  timeStyle: 'short',
});

const DATE_FORMATTER = new Intl.DateTimeFormat(LOCALE, {
  dateStyle: 'medium',
});

/** Texto exibido quando o dado simplesmente nao existe. Nunca "0" nem "-". */
export const VALOR_AUSENTE = '—';

/**
 * Centavos inteiros -> "R$ 1.234,56".
 *
 * A divisao por 100 acontece aqui e em nenhum outro lugar. E segura porque e o
 * ultimo passo antes do texto: o formatador arredonda para 2 casas, entao a
 * imprecisao binaria de 129.9 nunca chega a ser somada a nada. Acima da faixa
 * inteira segura o resultado deixaria de ser confiavel, e ai preferimos um
 * marcador visivel a um numero silenciosamente errado.
 */
export function formatBRLFromCents(cents: number): string {
  if (!Number.isSafeInteger(cents)) {
    return VALOR_AUSENTE;
  }
  return CURRENCY_FORMATTER.format(cents / 100);
}

/** Contagem com separador de milhar. Para colunas de tabela e rotulos exatos. */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) {
    return VALOR_AUSENTE;
  }
  return INTEGER_FORMATTER.format(value);
}

/**
 * Contagem compacta ("12,3 mil") para o valor grande de um cartao de KPI.
 *
 * Abaixo de 10 mil devolve o numero exato: compactar "8,4 mil" esconde 400
 * leads sem ganhar espaco nenhum.
 */
export function formatCountCompact(value: number): string {
  if (!Number.isFinite(value)) {
    return VALOR_AUSENTE;
  }
  if (Math.abs(value) < 10_000) {
    return INTEGER_FORMATTER.format(value);
  }
  return COMPACT_FORMATTER.format(value);
}

/**
 * Pontos-base inteiros -> "15,0%". 1500 bps = 15,00%.
 *
 * O backend manda bps justamente para nao trafegar 0.15 em float; a divisao por
 * 10000 e de exibicao, mesma logica do dinheiro.
 */
export function formatPercentFromBps(bps: number): string {
  if (!Number.isFinite(bps)) {
    return VALOR_AUSENTE;
  }
  return PERCENT_FORMATTER.format(bps / 10_000);
}

/**
 * Razao entre duas contagens INTEIRAS -> texto de percentual.
 *
 * Recebe os dois inteiros em vez de um percentual pronto para que a divisao
 * ocorra uma unica vez, na exibicao. Denominador zero devolve marcador: "0%" de
 * nada e uma afirmacao falsa sobre o funil.
 */
export function formatRatio(part: number, whole: number): string {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) {
    return VALOR_AUSENTE;
  }
  return PERCENT_FORMATTER.format(part / whole);
}

/** ISO-8601 -> "14/09/26, 09:32". Data invalida vira marcador, nunca "Invalid Date". */
export function formatDateTime(iso: string | null): string {
  const date = parseIso(iso);
  return date === null ? VALOR_AUSENTE : DATE_TIME_FORMATTER.format(date);
}

/** ISO-8601 -> "14 de set. de 2026". Usado no rodape de periodo do painel. */
export function formatDate(iso: string | null): string {
  const date = parseIso(iso);
  return date === null ? VALOR_AUSENTE : DATE_FORMATTER.format(date);
}

function parseIso(iso: string | null): Date | null {
  if (iso === null || iso === '') {
    return null;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Segundos -> "2min 13s" ou "47s".
 *
 * Duracao de ligacao raramente passa de alguns minutos, entao hora nao entra no
 * formato; se passar, o minuto acumula ("73min 05s") em vez de virar "1h" e
 * obrigar o operador a converter de cabeca.
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) {
    return VALOR_AUSENTE;
  }
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  if (minutes === 0) {
    return `${rest}s`;
  }
  return `${minutes}min ${String(rest).padStart(2, '0')}s`;
}

/**
 * Segundos -> "01:36". Formato de transporte de player de audio.
 *
 * Separado de `formatDuration` de proposito: numa lista, duracao se le melhor
 * por extenso ("2min 13s"); dentro de um player, o relogio precisa ter largura
 * constante para nao tremer a cada segundo.
 */
export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '00:00';
  }
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/**
 * Mascara o telefone para exibicao, preservando DDD e os 2 ultimos digitos.
 *
 * Espelha `maskPhone` de src/domain/phone.ts de proposito: o painel fica aberto
 * em tela de escritorio o dia inteiro e o operador nao precisa do numero
 * completo para acompanhar a fila -- quem liga e a IA. O suficiente para
 * reconhecer um caso em suporte, sem despejar dado pessoal na sala.
 */
export function maskPhoneDisplay(e164: string): string {
  const match = /^\+55(\d{2})(\d+)(\d{2})$/.exec(e164);
  if (match === null) {
    return '***';
  }
  // noUncheckedIndexedAccess: grupo de regex e `string | undefined` mesmo
  // quando o padrao garante a presenca. O fallback mantem o tipo honesto.
  const ddd = match[1] ?? '';
  const middle = match[2] ?? '';
  const tail = match[3] ?? '';
  return `+55 (${ddd}) ${'*'.repeat(middle.length)}${tail}`;
}

/**
 * Primeiro nome apenas.
 *
 * Mesma razao da mascara do telefone: "Maria S." identifica o lead na fila sem
 * expor o nome completo de quem abandonou um carrinho.
 */
export function shortenName(name: string | null): string {
  if (name === null) {
    return VALOR_AUSENTE;
  }
  const parts = name.trim().split(/\s+/).filter((part) => part.length > 0);
  const first = parts[0];
  if (first === undefined) {
    return VALOR_AUSENTE;
  }
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  const initial = last === undefined ? '' : ` ${last.charAt(0).toUpperCase()}.`;
  return `${first}${initial}`;
}

// -----------------------------------------------------------------------------
// Vocabulario
// -----------------------------------------------------------------------------

/*
 * Rotulos em Record com chave de uniao literal (e nao index signature): se o
 * backend acrescentar um status, o tsc aponta o Record incompleto em vez de
 * renderizar `undefined` na tela.
 */

export const PROVIDER_LABEL: Readonly<Record<Provider, string>> = {
  kiwify: 'Kiwify',
  hotmart: 'Hotmart',
  shopify: 'Shopify',
  generic: 'Webhook genérico',
  hubla: 'Hubla',
  eduzz: 'Eduzz',
  cakto: 'Cakto',
  greenn: 'Greenn',
};

export const CHANNEL_LABEL: Readonly<Record<CampaignChannel, string>> = {
  voice: 'Ligacao da IA',
  whatsapp: 'WhatsApp',
};

export const TIER_LABEL: Readonly<Record<RecoveryTier, string>> = {
  tier1_voice: 'Tier 1 - Voz',
  tier2_whatsapp: 'Tier 2 - WhatsApp',
  exhausted: 'Esgotado',
};

export const CAMPAIGN_STATUS_LABEL: Readonly<Record<CampaignStatus, string>> = {
  pending: 'Em andamento',
  answered: 'Atendida',
  no_answer: 'Sem resposta',
  voicemail: 'Caixa postal',
  sent: 'Enviada',
  failed: 'Falhou',
};

export const JOB_STATUS_LABEL: Readonly<Record<RecoveryJobStatus, string>> = {
  pending: 'Na fila',
  running: 'Em recuperacao',
  succeeded: 'Recuperado',
  aborted: 'Cancelado',
  failed: 'Falhou',
};

/**
 * Tom semantico de um estado.
 *
 * A cor de status e vocabulario reservado e NUNCA viaja sozinha: todo consumidor
 * renderiza o rotulo em texto junto do ponto colorido. Tres dos quatro tons
 * ficam abaixo de 3:1 no fundo claro, entao cor sozinha nao comunicaria estado
 * nem para quem enxerga todas as matizes.
 */
export type StatusTone = 'good' | 'warning' | 'serious' | 'critical' | 'neutral';

export function campaignStatusTone(status: CampaignStatus): StatusTone {
  switch (status) {
    case 'answered':
    case 'sent':
      return 'good';
    case 'pending':
      return 'neutral';
    case 'no_answer':
    case 'voicemail':
      return 'warning';
    case 'failed':
      return 'critical';
    default:
      return 'neutral';
  }
}

export function jobStatusTone(status: RecoveryJobStatus): StatusTone {
  switch (status) {
    case 'succeeded':
      return 'good';
    case 'running':
    case 'pending':
      return 'neutral';
    case 'aborted':
      return 'warning';
    case 'failed':
      return 'critical';
    default:
      return 'neutral';
  }
}
